import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDrawingStore } from '../../../stores/drawingStore';
import { AVAILABLE_TOOLS } from '../../../types/tools';
import { ToolButton } from './ToolButton';
// Note: Individual icon imports are unused here; icons are provided by AVAILABLE_TOOLS
import type { ToolType } from '../../../types/drawing';
import { isDragHandle } from '../../../interactions/drag';

interface MainToolbarProps {
  className?: string;
  onToolbarClick: () => void;
  onPositionChange: (position: { x: number; y: number }) => void;
}

export const MainToolbar: React.FC<MainToolbarProps> = ({ className = '', onToolbarClick, onPositionChange }) => {
  const { currentTool, setCurrentTool } = useDrawingStore();
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Notify parent of position changes
  useEffect(() => {
    onPositionChange(position);
  }, [position, onPositionChange]);

  // Handle mouse down for dragging
  const handleMouseDown = (event: React.MouseEvent) => {
    // Check if clicking on the drag handler area
    if (isDragHandle(event.target)) {
      setIsDragging(true);
      const rect = toolbarRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) return;

    const newX = event.clientX - dragOffset.x;
    const newY = event.clientY - dragOffset.y;

    // Keep toolbar within viewport bounds
    const maxX = window.innerWidth - (toolbarRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (toolbarRef.current?.offsetHeight || 0);

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, dragOffset]);

  // Handle mouse up for dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle double click on drag handler to reset to default position
  const handleDoubleClick = (event: React.MouseEvent) => {
    if (isDragHandle(event.target)) {
      setPosition({ x: 20, y: 20 }); // Reset to default position
    }
  };

  // Setup global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, handleMouseMove, handleMouseUp]);

  // Handle tool selection
  const handleToolSelect = (toolId: string) => {
    setCurrentTool(toolId as ToolType);
    // Only show secondary toolbar for drawing tools, not for hand tool
    if (toolId !== 'hand') {
      onToolbarClick();
    }
  };

  return (
    <div
      ref={toolbarRef}
      className={`fixed z-50 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl p-3 select-none ${className}`}
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Drag Handler - 6 dots in 2 rows with larger draggable area */}
      <div className="drag-handler w-full h-4 mb-3 flex items-center justify-center cursor-move hover:cursor-move group">
        <div className="flex flex-col space-y-1">
          {/* Top row - 3 dots */}
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
          </div>
          {/* Bottom row - 3 dots */}
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col gap-2">
        {/* All tools including hand and select */}
        {AVAILABLE_TOOLS.map((tool) => (
          <ToolButton
            key={tool.id}
            name={tool.name}
            icon={tool.icon}
            shortcut={tool.shortcut}
            isActive={currentTool === tool.id}
            onClick={() => handleToolSelect(tool.id)}
          />
        ))}
      </div>
    </div>
  );
};
